import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
export type CellData = string | number | React.ReactNode | undefined;

export interface Filter {
    name: string;
    filter: (cell: CellData[]) => boolean;
}

interface TableProps {
    data: CellData[][];
    header?: React.ReactNode[];
    filters?: Filter[];
    rowKeyIndex?: number;
    selectedKey?: string;
    onSelect?: (key: string) => void;
    titleComponent?: React.ReactNode;
    rowBackground?: (row: number) => React.CSSProperties;
    side?: 'left' | 'right';
    rowBackgroundColor?: string;
    colSpan?: number;
}

const Table: React.FC<TableProps> = ({
    data,
    header,
    filters = [],
    rowKeyIndex,
    selectedKey,
    onSelect,
    titleComponent,
    rowBackground,
    side = 'left',
    rowBackgroundColor = 'rgba(184, 233, 245, 0.7)',
    colSpan,
}) => {
    const [activeFilter, setActiveFilter] = useState<string | undefined>(undefined);
    const [resultData, setResultData] = useState<CellData[][] | undefined>(undefined);
    const [selectedRowKey, setSelectedRowKey] = useState<string | undefined>(selectedKey);

    useEffect(() => {
        if (filters.length > 0) {
            handleFilter(filters[0]);
        }
    }, [filters]);

    useEffect(() => {
        setSelectedRowKey(selectedKey);
    }, [selectedKey]);

    useEffect(() => {
        if (filters.length > 0 && activeFilter) {
            const filter = filters.find((f) => f.name === activeFilter);
            if (filter) {
                handleFilter(filter);
            }
        }
    }, [data]);

    const handleFilter = (item: Filter) => {
        if (!item.filter) {
            setResultData(data);
            return;
        }
        const filteredData = [...data].filter(item.filter);
        setActiveFilter(item.name);
        setResultData(filteredData);
    };

    const handleSelect = (key: string) => {
        setSelectedRowKey(key);
        if (onSelect) {
            onSelect(key);
        }
    };

    // const ensureDataIsValid = (data: CellData[][]) => {
    //     const length = data[0].length;
    //     data.forEach((row) => {
    //         if (row.length !== length) {
    //             throw Error('Array elements must have the same length');
    //         }
    //     });
    // };

    const renderTitleComponent = () => <div className="cr-title-component">{titleComponent}</div>;

    const renderRowCells = (row: CellData[]) =>
        row?.map((c, index) => (
            <td key={index} colSpan={row.length === 1 ? colSpan : undefined}>
                {c}
            </td>
        ));

    const renderFilters = () =>
        filters.map((item) => {
            const cn = classNames('cr-table__filter', {
                'cr-table__filter--active': activeFilter === item.name,
            });

            return (
                <div className={cn} key={item.name} onClick={() => handleFilter(item)}>
                    {item.name}
                </div>
            );
        });

    const renderHead = (row: CellData[]) => {
        return (
            <thead className="cr-table__head">
                <tr className="cr-table__head-row">
                    {row.map((c, index) => (c ? <th key={index}>{c}</th> : <th key={index}>&nbsp;</th>))}
                </tr>
            </thead>
        );
    };

    const renderRowBackground = (i: number) => {
        const style = {
            ...rowBackground?.(i),
            backgroundColor: rowBackgroundColor,
        };

        return rowBackground ? <span key={i} style={style} className="cr-table-background__row" /> : null;
    };

    const renderBackground = () => {
        const dataToRender = resultData || data;

        const className = classNames('cr-table-background', {
            'cr-table-background--left': side === 'left',
            'cr-table-background--right': side === 'right',
        });

        return <div className={className}>{rowBackground && dataToRender.map((_, i) => renderRowBackground(i))}</div>;
    };
    const renderBody = () => {
        const dataToRender = resultData || data;

        return (
            <tbody className="cr-table__body">
                {dataToRender
                    .filter((item) => item[0] !== '' && typeof item[0] == 'string')
                    .map((row, i) => {
                        const ticker = row[0] as string;
                        const rowClassName = classNames({
                            'cr-table__row--selected': selectedRowKey === ticker,
                        });
                        return (
                            <tr key={ticker} className={rowClassName} onClick={() => handleSelect(ticker)}>
                                {renderRowCells(row)}
                            </tr>
                        );
                    })}
            </tbody>
        );
    };

    // ensureDataIsValid(data);

    const cn = classNames('cr-table-header__content', {
        'cr-table-header__content-empty': !titleComponent && filters.length === 0,
    });

    return (
        <div className="cr-table-container">
            <div className={cn}>
                {titleComponent && renderTitleComponent()}
                {filters.length > 0 && <div className="cr-table__filters">{renderFilters()}</div>}
            </div>
            <table className="cr-table">
                {header && header.length > 0 && renderHead(header)}
                {renderBody()}
            </table>
            {renderBackground()}
        </div>
    );
};

export { Table };
